import {
  Space,
  Table,
  List,
  CreateButton,
  DeleteButton,
  Drawer,
  Form,
  Input,
  Button,
  Grid,
  notification,
} from '@pankod/refine-antd';
import { useEffect, useState } from 'react';
import { IPointOfContact } from 'interfaces';
import { EditPointOfContact } from './edit';
import { getAllPointOfContacts, createPointOfContact } from 'services/point-of-contact';
import { capitalize } from 'utilities/shared';
import { useCan } from '@pankod/refine-core';

type PointOfContactTableProps = {
  resource: string;
  resourceId: string;
};

export const PointOfContactTable: React.FC<PointOfContactTableProps> = (props) => {
  const [pointOfContacts, setPointOfContacts] = useState<IPointOfContact[]>([]);
  const [visible, setVisible] = useState<boolean>(false);
  const [form] = Form.useForm();
  const breakpoint = Grid.useBreakpoint();
  const capitalizedResource = capitalize(props.resource);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAllPointOfContacts(props.resource, props.resourceId);
        setPointOfContacts(data);
      } catch (err) {
        return;
      }
    })();
  }, [props.resource, props.resourceId]);

  const resource = window.location.pathname.split('/')[1].toLowerCase();
  const { data: canAccessDelete } = useCan({
    resource: resource,
    action: 'delete',
    params: { field: 'id' },
  });

  const showDrawer = () => {
    setVisible(true);
  };

  const closeDrawer = () => {
    setVisible(false);
  };

  const onDeleteSuccessHandler = async () => {
    try {
      const data = await getAllPointOfContacts(props.resource, props.resourceId);
      setPointOfContacts(data);
    } catch (err) {
      return notification.error({
        message: 'Failed',
        description: 'Failed to delete.',
      });
    }
  };

  const onFinish = async (values: Omit<IPointOfContact, 'id'>) => {
    try {
      await createPointOfContact(props.resource, values);
      const data = await getAllPointOfContacts(props.resource, props.resourceId);
      setPointOfContacts(data);
      notification.success({
        message: 'Success',
        description: 'Successfully created point of contact.',
      });
      form.resetFields();
      setVisible(false);
    } catch (err) {
      return notification.error({
        message: 'Failed',
        description: 'Creation failed.',
      });
    }
  };

  return (
    <>
      <List
        title={`${capitalizedResource} Point Of Contact`}
        headerProps={{
          extra: <CreateButton type="primary" onClick={showDrawer}>Add New</CreateButton>,
        }}
      >
        <Table dataSource={pointOfContacts} rowKey="id" scroll={{ x: true }}>
          <Table.Column dataIndex="name" title="Name" />
          <Table.Column dataIndex="designation" title="Designation" />
          <Table.Column dataIndex="email" title="Email" />
          <Table.Column dataIndex="mobileNumber" title="Phone" />
          <Table.Column<IPointOfContact>
            dataIndex="actions"
            title="Actions"
            render={(_, pocRecord): React.ReactNode => {
              return (
                <Space>
                  <EditPointOfContact
                    resource={props.resource}
                    resourceId={props.resourceId}
                    pointOfContact={pocRecord}
                    setPointOfContacts={setPointOfContacts}
                  />
                  <DeleteButton
                    size="small"
                    hideText
                    resourceName={`${props.resource}POC`}
                    recordItemId={pocRecord?.id}
                    onSuccess={onDeleteSuccessHandler}
                    disabled={!canAccessDelete?.can}
                  />
                </Space>
              );
            }}
          />
        </Table>
      </List>
      <Drawer
        title="Add New Point Of Contact"
        placement="right"
        width={breakpoint.sm ? '450px' : '100%'}
        visible={visible}
        onClose={closeDrawer}
      >
        <Form
          layout="vertical"
          initialValues={{
            [props.resource]: props.resourceId,
            isActive: true,
          }}
          form={form}
          onFinish={onFinish}
        >
          <Form.Item
            label={`${props.resource} ID`}
            name={props.resource}
            rules={[
              {
                required: true,
              },
            ]}
            style={{ display: 'none' }}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            label="Name"
            name="name"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Designation" name="designation">
            <Input />
          </Form.Item>
          <Form.Item
            label="Phone"
            name="mobileNumber"
            rules={[
              {
                required: true,
              },
              {
                pattern: /^(\+\d{1,3}[- ]?)?\d{8,12}$/,
                message: 'Provide valid number',
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};
